import { useMemo, useRef, useState } from 'react';
import {
  CORRIGO_ALLOWED_DOCUMENT_EXTENSIONS,
  CORRIGO_ALLOWED_DOCUMENT_MIME_TYPES,
  CORRIGO_DOCUMENT_TYPES,
  CORRIGO_MAX_DOCUMENT_BYTES,
  type CorrigoDocumentType,
  type DocumentUploadResult,
  type RequiredDocumentSlot,
  type WorkOrderDocumentTargets,
} from '@tcg/shared';
import { ApiClientError, documentsApi } from '../lib/api';
import { UPLOAD_CONCURRENCY, mapWithConcurrency } from '../lib/concurrency';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Spinner } from './ui';

/**
 * Drag-and-drop document uploader for a Corrigo work order.
 *
 * - Multiple files, click-to-browse or drop. Files are STAGED IN MEMORY and only
 *   uploaded on explicit "Upload" (never on drop, no browser storage).
 * - When the work order has Required Document slots, each slot gets its own
 *   labeled dropzone PLUS a general "Additional documents" zone; otherwise only
 *   the general zone. A file is bound to the zone it was dropped on (and can be
 *   re-targeted per file before upload).
 * - Client-side blocks >20 MB and disallowed types (server re-validates).
 * - Per-file status (queued/uploading/done/error) + progress; a partial failure
 *   is isolated so just that file can be retried — the quote is never resubmitted.
 */

const GENERAL = 'general';

type FileStatus = 'queued' | 'uploading' | 'done' | 'error';

interface Staged {
  id: string;
  file: File;
  /** GENERAL or a slot's documentId. */
  target: string;
  documentType: CorrigoDocumentType;
  status: FileStatus;
  progress: number;
  error?: string;
  /** Client-side validation failure — blocks upload until removed/fixed. */
  invalid?: string;
  result?: DocumentUploadResult;
}

const STATUS_TONE: Record<RequiredDocumentSlot['status'], 'green' | 'amber' | 'slate'> = {
  Incomplete: 'amber',
  NeedsReview: 'amber',
  NoReviewNeeded: 'slate',
  Reviewed: 'green',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

/** Mirror the server's accept rule: known MIME type OR known extension. */
function clientValidate(file: File): string | undefined {
  if (file.size > CORRIGO_MAX_DOCUMENT_BYTES) return 'Larger than 20 MB';
  if (file.size === 0) return 'File is empty';
  const mimeOk = CORRIGO_ALLOWED_DOCUMENT_MIME_TYPES.includes(file.type.toLowerCase());
  const extOk = CORRIGO_ALLOWED_DOCUMENT_EXTENSIONS.includes(fileExtension(file.name));
  if (!mimeOk && !extOk) return 'Unsupported type (allowed: PDF, JPG, PNG, DOC, DOCX)';
  return undefined;
}

export function DocumentDropzone({
  targets,
  onUploaded,
}: {
  targets: WorkOrderDocumentTargets;
  onUploaded?: (result: DocumentUploadResult) => void;
}) {
  const [staged, setStaged] = useState<Staged[]>([]);
  const [uploading, setUploading] = useState(false);
  const idSeq = useRef(0);

  const slots = targets.requiredDocuments;
  const hasSlots = targets.hasRequiredDocuments;

  const byTarget = useMemo(() => {
    const map = new Map<string, Staged[]>();
    for (const s of staged) {
      const arr = map.get(s.target) ?? [];
      arr.push(s);
      map.set(s.target, arr);
    }
    return map;
  }, [staged]);

  const pendingCount = staged.filter((s) => !s.invalid && (s.status === 'queued' || s.status === 'error')).length;
  const invalidCount = staged.filter((s) => s.invalid).length;
  const doneCount = staged.filter((s) => s.status === 'done').length;

  const addFiles = (files: FileList | File[], target: string) => {
    const next: Staged[] = Array.from(files).map((file) => ({
      id: `f${idSeq.current++}`,
      file,
      target,
      documentType: 'Document',
      status: 'queued' as FileStatus,
      progress: 0,
      invalid: clientValidate(file),
    }));
    setStaged((prev) => [...prev, ...next]);
  };

  const update = (id: string, patch: Partial<Staged>) =>
    setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const remove = (id: string) => setStaged((prev) => prev.filter((s) => s.id !== id));

  const uploadOne = async (item: Staged) => {
    if (item.invalid) return;
    update(item.id, { status: 'uploading', progress: 0, error: undefined });
    const onProgress = (fraction: number) => update(item.id, { progress: fraction });
    try {
      const result =
        item.target === GENERAL
          ? await documentsApi.uploadGeneral(
              targets.woNumber,
              item.file,
              { documentType: item.documentType, workOrderId: targets.workOrderId },
              onProgress,
            )
          : await documentsApi.uploadRequired(
              targets.woNumber,
              item.target,
              item.file,
              { documentType: item.documentType, workOrderId: targets.workOrderId },
              onProgress,
            );
      update(item.id, { status: 'done', progress: 1, result });
      onUploaded?.(result);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Upload failed';
      update(item.id, { status: 'error', error: message });
    }
  };

  const uploadAll = async () => {
    // Snapshot the items to upload (queued or previously errored, still valid).
    const toUpload = staged.filter(
      (s) => !s.invalid && (s.status === 'queued' || s.status === 'error'),
    );
    if (toUpload.length === 0) return;
    setUploading(true);
    try {
      // Each file is an independent request; a failure of one doesn't abort
      // others. Bounded pool so a large batch doesn't saturate Corrigo's upload
      // endpoint and time out under its own load.
      await mapWithConcurrency(toUpload, UPLOAD_CONCURRENCY, (item) => uploadOne(item));
    } finally {
      setUploading(false);
    }
  };

  if (!targets.canUpload) {
    return <Banner tone="error">{targets.reason ?? 'Documents cannot be attached to this work order.'}</Banner>;
  }

  return (
    <div className="space-y-4">
      {hasSlots && (
        <Banner tone="info">
          This work order has <strong>{slots.length}</strong> required document
          {slots.length === 1 ? '' : 's'}. Drop the matching file on each slot below; use{' '}
          <em>Additional documents</em> for anything else.
        </Banner>
      )}

      {slots.map((slot) => (
        <SlotZone
          key={slot.documentId}
          slot={slot}
          items={byTarget.get(slot.documentId) ?? []}
          onAdd={(files) => addFiles(files, slot.documentId)}
          onUpdate={update}
          onRemove={remove}
          onRetry={uploadOne}
          targets={targets}
          disabled={uploading}
        />
      ))}

      <GeneralZone
        items={byTarget.get(GENERAL) ?? []}
        onAdd={(files) => addFiles(files, GENERAL)}
        onUpdate={update}
        onRemove={remove}
        onRetry={uploadOne}
        targets={targets}
        disabled={uploading}
      />

      {invalidCount > 0 && (
        <Banner tone="warn">
          {invalidCount} file{invalidCount === 1 ? '' : 's'} can’t be uploaded (see the reasons above).
          Remove {invalidCount === 1 ? 'it' : 'them'} to continue.
        </Banner>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {staged.length === 0
            ? 'No files staged yet.'
            : `${pendingCount} to upload · ${doneCount} done${invalidCount ? ` · ${invalidCount} blocked` : ''}`}
        </div>
        <Button onClick={() => void uploadAll()} disabled={uploading || pendingCount === 0}>
          {uploading ? <Spinner /> : `Upload ${pendingCount} file${pendingCount === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  );
}

// ── Zones ─────────────────────────────────────────────────────────────────────

interface ZoneCommon {
  items: Staged[];
  onAdd: (files: FileList | File[]) => void;
  onUpdate: (id: string, patch: Partial<Staged>) => void;
  onRemove: (id: string) => void;
  onRetry: (item: Staged) => void;
  targets: WorkOrderDocumentTargets;
  disabled: boolean;
}

function SlotZone({ slot, ...common }: { slot: RequiredDocumentSlot } & ZoneCommon) {
  const label = slot.title || slot.description || `Required document ${slot.documentId}`;
  return (
    <Card>
      <CardHeader
        title={label}
        subtitle={slot.description && slot.description !== label ? slot.description : 'Required document slot'}
        action={<Badge tone={STATUS_TONE[slot.status]}>{slot.status}</Badge>}
      />
      <CardBody className="space-y-3">
        {slot.attachment && (
          <Banner tone="success">
            Already on file: <strong>{slot.attachment.name ?? slot.attachment.attachmentId}</strong>
            {slot.attachment.senderName ? ` · ${slot.attachment.senderName}` : ''}
          </Banner>
        )}
        <DropArea {...common} label="Drop the file for this slot or click to browse" />
        <FileList {...common} />
      </CardBody>
    </Card>
  );
}

function GeneralZone(common: ZoneCommon) {
  return (
    <Card>
      <CardHeader
        title="Additional documents"
        subtitle="Supporting files not tied to a required-document slot."
        action={<Badge tone="slate">General</Badge>}
      />
      <CardBody className="space-y-3">
        <DropArea {...common} label="Drop files here or click to browse" />
        <FileList {...common} />
      </CardBody>
    </Card>
  );
}

function DropArea({
  onAdd,
  label,
  disabled,
}: ZoneCommon & { label: string }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const accept = CORRIGO_ALLOWED_DOCUMENT_EXTENSIONS.join(',');

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (disabled) return;
        if (e.dataTransfer.files?.length) onAdd(e.dataTransfer.files);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition ${
        over ? 'border-accent-500 bg-accent-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onAdd(e.target.files);
          e.target.value = '';
        }}
      />
      <span className="font-medium text-slate-600">{label}</span>
      <span className="mt-1 text-xs text-slate-400">PDF, JPG, PNG, DOC, DOCX · up to 20 MB each</span>
    </div>
  );
}

function FileList({ items, onUpdate, onRemove, onRetry, disabled }: ZoneCommon) {
  if (items.length === 0) return null;
  return (
    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
      {items.map((item) => (
        <li key={item.id} className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-slate-800" title={item.file.name}>
                {item.file.name}
              </span>
              <span className="shrink-0 text-xs text-slate-400">{formatBytes(item.file.size)}</span>
              <StatusBadge item={item} />
            </div>
            {item.invalid && <p className="mt-0.5 text-xs text-red-600">{item.invalid}</p>}
            {item.error && <p className="mt-0.5 text-xs text-red-600">{item.error}</p>}
            {item.status === 'uploading' && (
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full bg-accent-500 transition-all"
                  style={{ width: `${Math.round(item.progress * 100)}%` }}
                />
              </div>
            )}
            {item.status === 'done' && item.result?.attachmentId && (
              <p className="mt-0.5 text-xs text-slate-400">AttachmentId {item.result.attachmentId}</p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {item.status !== 'done' && !item.invalid && (
              <select
                value={item.documentType}
                onChange={(e) => onUpdate(item.id, { documentType: e.target.value as CorrigoDocumentType })}
                disabled={disabled || item.status === 'uploading'}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                title="Document type"
              >
                {CORRIGO_DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            )}
            {item.status === 'error' && !item.invalid && (
              <Button variant="secondary" onClick={() => onRetry(item)} disabled={disabled}>
                Retry
              </Button>
            )}
            {item.status !== 'done' && item.status !== 'uploading' && (
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                disabled={disabled}
                className="text-xs text-slate-400 hover:text-red-600 disabled:opacity-50"
                title="Remove"
              >
                ✕
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ item }: { item: Staged }) {
  if (item.invalid) return <Badge tone="red">blocked</Badge>;
  switch (item.status) {
    case 'queued':
      return <Badge tone="slate">queued</Badge>;
    case 'uploading':
      return <Badge tone="amber">{Math.round(item.progress * 100)}%</Badge>;
    case 'done':
      return <Badge tone="green">done</Badge>;
    case 'error':
      return <Badge tone="red">error</Badge>;
  }
}
